// utils/storage.ts

// --- 1. COOKIE HELPERS (For Auth Only) ---

const getCookieDomain = () => {
  if (typeof window === 'undefined') return undefined;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return undefined;
  }
  if (hostname.endsWith('workhorse.com.sg')) {
    return '.workhorse.com.sg';
  }
  return undefined; 
};

const getSecureFlag = () => {
  if (typeof window === 'undefined') return '';
  return window.location.protocol === 'https:' ? 'Secure;' : '';
};

const COOKIE_DOMAIN = getCookieDomain();
const SECURE_FLAG = getSecureFlag();

const setSharedCookie = (name: string, value: string, hours: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();

  let cookieString = `${name}=${value};${expires};path=/;SameSite=Lax;${SECURE_FLAG}`;

  if (COOKIE_DOMAIN) {
    cookieString += `domain=${COOKIE_DOMAIN};`;
  }

  document.cookie = cookieString;
};

// Exported for general use if needed
export const getCookie = (name: string) => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  const domainAttr = COOKIE_DOMAIN ? `domain=${COOKIE_DOMAIN};` : '';
  document.cookie = `${name}=; Max-Age=-99999999; ${domainAttr} path=/;`;
  document.cookie = `${name}=; Max-Age=-99999999; path=/;`;
};

// --- HELPER: Generate UUID ---
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};


// --- 2. LOCAL STORAGE HELPERS (For Session ID) ---

export const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  // CHANGED: Now reads from localStorage instead of Cookie
  return localStorage.getItem('sessionId');
};

export const saveSessionId = (sessionId: string) => {
  if (typeof window === 'undefined') return;
  // CHANGED: Now writes to localStorage. No expiration needed (persistent by default).
  localStorage.setItem('sessionId', sessionId);
};


// --- EXPORTED AUTH FUNCTIONS ---

export const saveAuth = (token: string, user: any) => {
  // ----------------------------------------------------------------------
  // CHANGES START HERE: Updated all expiration values to 48 hours (2 days)
  // ----------------------------------------------------------------------

  // Auth tokens still go to Cookies (better security/SSR support)
  // CHANGED: Replaced "1" with "48"
  setSharedCookie('token', token, 48);
  
  // CHANGED: Replaced (60 * 60 * 1000) with (48 * 60 * 60 * 1000) for 48 hours
  const expirationTime = Date.now() + (48 * 60 * 60 * 1000); 
  
  // CHANGED: Replaced "1" with "48"
  setSharedCookie('tokenExpiration', expirationTime.toString(), 48);

  if (user) {
    // CHANGED: Replaced "1" with "48"
    setSharedCookie('user', encodeURIComponent(JSON.stringify(user)), 48);
  }

  // ----------------------------------------------------------------------
  // CHANGES END HERE
  // ----------------------------------------------------------------------
};

export const ensureAnonymousSession = () => {
  // 1. Check LocalStorage
  const currentSessionId = getSessionId();
  
  if (currentSessionId) {
    return currentSessionId;
  }

  // 2. Generate & Save to LocalStorage
  const newSessionId = generateUUID();
  saveSessionId(newSessionId);
  
  return newSessionId;
};

export const clearAuth = () => {
  // CRITICAL: Since sessionId is now in localStorage, `localStorage.clear()` would wipe it.
  // We must preserve it to maintain device tracking across logouts.
  
  const currentSessionId = getSessionId(); // 1. Save ID

  deleteCookie('token');
  deleteCookie('user');
  deleteCookie('tokenExpiration');
  
  // Only clearing sessionStorage here so we don't accidentally wipe localStorage 
  // where the persistent sessionId is stored.
  sessionStorage.clear();

  if (currentSessionId) {
    saveSessionId(currentSessionId); // 3. Restore ID
  }
};

export const isTokenValid = () => {
  const token = getCookie('token'); 
  const expirationStr = getCookie('tokenExpiration');
  
  if (!token || !expirationStr) {
    return false; 
  }

  const expiration = parseInt(expirationStr, 10);
  const now = Date.now();
  
  return now < expiration;
};

export const getUser = () => {
  const userStr = getCookie('user');
  if (userStr) {
    try {
      return JSON.parse(decodeURIComponent(userStr));
    } catch (e) {
      return null;
    }
  }
  return null;
};

export const hardResetSession = () => {
  // This is a full reset, so we WANT to wipe the sessionId from localStorage
  deleteCookie('token');
  deleteCookie('user');
  deleteCookie('tokenExpiration');

  // Also clear everything from local and session storage
  localStorage.clear();
  sessionStorage.clear();
};